import { createContext, useContext, useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const ChatbotContext = createContext();

export const ChatbotProvider = ({ children }) => {
  const { pathname } = useLocation();
  const isCalendarPage = pathname === "/calendar";
  const prevPathRef = useRef(pathname);

  // Default to expanded on calendar page
  const [isExpanded, setIsExpanded] = useState(isCalendarPage);
  // Track when chatbot is floating/detached from sidebar
  const [isDetached, setIsDetached] = useState(false);
  // Track floating position (persists across component mounts)
  const [floatingPosition, setFloatingPosition] = useState({ x: 0, y: 0 });
  // Track dragging state (persists across component mounts for smooth drag)
  const [isDragging, setIsDragging] = useState(false);
  // Track drag offset (persists across component mounts)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });


  // When navigating TO calendar page (not just being on it), open and dock chatbot
  useEffect(() => {
    const wasOnCalendarPage = prevPathRef.current === "/calendar";
    const isNowOnCalendarPage = isCalendarPage;

    // Only dock when ENTERING the calendar page, not when already on it
    if (isNowOnCalendarPage && !wasOnCalendarPage) {
      // Use setTimeout to ensure this runs after sidebar expansion
      setTimeout(() => {
        setIsExpanded(true);
        setIsDetached(false);
        setFloatingPosition({ x: 0, y: 0 });
        setIsDragging(false);
      }, 0);
    }

    // Update the ref for next comparison
    prevPathRef.current = pathname;
  }, [pathname, isCalendarPage]);

  const toggleChatbot = () => {
    setIsExpanded(prev => !prev);
  };

  const openChatbot = () => setIsExpanded(true);
  const closeChatbot = () => setIsExpanded(false);

  // Detach chatbot from sidebar (for drag start)
  const detachChatbot = () => setIsDetached(true);

  // Dock chatbot back to sidebar
  const dockChatbot = () => {
    setIsDetached(false);
    setFloatingPosition({ x: 0, y: 0 });
  };

  return (
    <ChatbotContext.Provider
      value={{
        isExpanded,
        setIsExpanded,
        isDetached,
        setIsDetached,
        floatingPosition,
        setFloatingPosition,
        isDragging,
        setIsDragging,
        dragOffset,
        setDragOffset,
        toggleChatbot,
        openChatbot,
        closeChatbot,
        detachChatbot,
        dockChatbot,
        isCalendarPage
      }}
    >
      {children}
    </ChatbotContext.Provider>
  );
};

export const useChatbot = () => {
  const context = useContext(ChatbotContext);
  if (!context) {
    throw new Error("useChatbot must be used within a ChatbotProvider");
  }
  return context;
};
